import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { CustomModal, useDevice, useOpenUnsavedChangesModal } from 'seagull';
import { ClientOnboarding } from '~/client-flow/clientOb/ClientOnboarding';
import { COBStep, PreviewState } from '~/client-flow/clientOb/ClientOnboarding.utils';
import { CobBrandingProvider, OverrideBranding, useCobBranding } from '~/client-flow/clientOb/CobBrandingContext';
import { CobStorageProvider, mockStorageImpl } from '~/client-flow/clientOb/CobStorageContext';
import { useIoContext, useStoreActions, useStoreStatic } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import RestfulWrapper from '../RestfulWrapper/RestfulWrapper';
import { getRelationshipFromDraft, getSetupBrandRelationship } from './ClientPreview.utils';
function UnsavedChangesPopup(param) {
    let { registerCanDismiss, isInBrandSetup } = param;
    const { isDirty } = useCobBranding();
    const { logBI } = useStoreActions();
    const { SET_UP_BRAND_CLIENT_PREVIEW = false } = useFeatureFlags();
    const openUnsavedChangesModal = useOpenUnsavedChangesModal();
    registerCanDismiss(async ()=>{
        if (!isDirty) return true;
        const confirmed = await openUnsavedChangesModal({
            testId: 'client-preview-unsaved-popup',
            title: isInBrandSetup() && SET_UP_BRAND_CLIENT_PREVIEW ? 'Unsaved brand changes' : undefined
        });
        if (confirmed) {
            logBI(11220);
        }
        return confirmed;
    });
    return null;
}
function ClientPreviewWithRelationship(props) {
    const { isSetupBrand, dismissModal, registerCanDismiss } = props;
    const [currentStep, setCurrentStep] = useState(COBStep.welcome);
    const [relationship, setRelationship] = useState(props.relationship);
    const [currentPreviewState, setCurrentPreviewState] = useState(isSetupBrand ? PreviewState.SetupBrand : PreviewState.Preview);
    const [deviceView, setDeviceView] = useState('desktop');
    const { isMobileDevice } = useDevice();
    const { CLIENT_PREVIEW_DEVICE_TOGGLE = false } = useFeatureFlags();
    function updateRelationshipLogo(logoUrl) {
        setRelationship((r)=>relationshipModel.setVendorLogo(r, logoUrl));
    }
    const isInBrandSetup = ()=>currentPreviewState === PreviewState.SetupBrand;
    return /*#__PURE__*/ _jsx(CobStorageProvider, {
        impl: mockStorageImpl,
        children: /*#__PURE__*/ _jsxs(CobBrandingProvider, {
            onLogoChange: updateRelationshipLogo,
            children: [
                /*#__PURE__*/ _jsx(OverrideBranding, {
                    children: /*#__PURE__*/ _jsx(UnsavedChangesPopup, {
                        registerCanDismiss: registerCanDismiss,
                        isInBrandSetup: isInBrandSetup
                    })
                }),
                /*#__PURE__*/ _jsx(ClientOnboarding, {
                    disableMobile: CLIENT_PREVIEW_DEVICE_TOGGLE ? deviceView === 'desktop' : !isMobileDevice,
                    previewState: isSetupBrand ? PreviewState.SetupBrand : PreviewState.Preview,
                    onPreviewStateChange: setCurrentPreviewState,
                    dismissPreviewModal: dismissModal,
                    relationship: relationship,
                    currentStep: currentStep,
                    setCurrentStep: setCurrentStep,
                    refresh: props.refresh,
                    deviceView: CLIENT_PREVIEW_DEVICE_TOGGLE ? deviceView : undefined,
                    onDeviceViewChange: CLIENT_PREVIEW_DEVICE_TOGGLE ? setDeviceView : undefined
                })
            ]
        })
    });
}
export function ClientPreview(props) {
    const { type, dismissModal, registerCanDismiss } = props;
    const { api } = useStoreActions();
    const { getState } = useStoreStatic();
    const { getNow } = useIoContext();
    const { generateUniqueId } = useAppConfig();
    const { SET_UP_BRAND_CLIENT_PREVIEW = false } = useFeatureFlags();
    const { isMobileDevice } = useDevice();
    const dimensions = isMobileDevice ? {
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        mobileMaxWidth: '100%'
    } : {
        width: '90%',
        height: '90%',
        minWidth: '1200px',
        maxWidth: 'auto',
        maxHeight: 'auto'
    };
    return /*#__PURE__*/ _jsx(CustomModal, _object_spread_props(_object_spread({}, dimensions), {
        onClose: dismissModal,
        testId: "client-preview-modal",
        fullSizeHeader: true,
        hideCloseBtn: SET_UP_BRAND_CLIENT_PREVIEW,
        children: /*#__PURE__*/ _jsx(RestfulWrapper, {
            fetch: async ()=>{
                if (type === 'draft') {
                    return getRelationshipFromDraft(_object_spread_props(_object_spread({}, props.draftInfo), {
                        now: getNow(),
                        userInfo: getState().userInfo,
                        worksAt: selectors.user.business(getState()),
                        generateUniqueId
                    }));
                } else if (type === 'relationshipId') {
                    return await api.relationship.get(props.relationshipId);
                } else if (type === 'originalProposal') {
                    return await api.relationship.getOriginalProposal(props.relationshipId);
                } else {
                    return getSetupBrandRelationship({
                        legalTerms: props.setupBrandInfo.legalTerms,
                        userInfo: getState().userInfo,
                        worksAt: selectors.user.business(getState())
                    });
                }
            },
            renderChild: (param)=>{
                let { data, refresh } = param;
                return /*#__PURE__*/ _jsx(ClientPreviewWithRelationship, {
                    isSetupBrand: type === 'setupBrand',
                    relationship: data,
                    dismissModal: dismissModal,
                    registerCanDismiss: registerCanDismiss,
                    refresh: refresh
                });
            }
        })
    }));
}
